import {
  API_BASE_URL,
  apiGet,
  apiGetResponse,
  apiPatch,
  apiPatchFormData,
  apiPost,
  getAccessToken,
  getCookie,
  handleResponse,
} from "../../client";
import { ApiConfig } from "../../config";
import type {
  LoginResponse,
  RequiredActionResponse,
  UpdateProfileResponse,
  UserProfile,
} from "./account.types";

export async function login(
  username: string,
  password: string,
): Promise<LoginResponse> {
  return apiPost<LoginResponse>(
    `${ApiConfig.ACCOUNT_BASE}/login/`,
    {
      username,
      password,
    },
    { withAuth: false },
  );
}

export async function whoAmI(): Promise<UserProfile> {
  return apiGet<UserProfile>(`${ApiConfig.ACCOUNT_BASE}/whoami/`);
}

export async function resetPassword(
  newPassword: string,
  confirmPassword: string,
): Promise<RequiredActionResponse> {
  return apiPost<RequiredActionResponse>(
    `${ApiConfig.ACCOUNT_BASE}/password/reset/`,
    {
      new_password: newPassword,
      confirm_password: confirmPassword,
    },
  );
}

export async function updateLicenseNumber(
  licenseNumber: string,
): Promise<RequiredActionResponse> {
  return apiPatch<RequiredActionResponse>(
    `${ApiConfig.ACCOUNT_BASE}/profile/license/`,
    {
      license_number: licenseNumber,
    },
  );
}

export async function updateProfileImage(
  profileImage: File,
): Promise<RequiredActionResponse> {
  const formData = new FormData();
  formData.append("profile_image", profileImage);
  return apiPatchFormData<RequiredActionResponse>(
    `${ApiConfig.ACCOUNT_BASE}/profile/image/`,
    formData,
  );
}

export async function previewProfileImage(profileImage: File): Promise<Blob> {
  const formData = new FormData();
  formData.append("profile_image", profileImage);

  const csrftoken = getCookie("csrftoken");
  const bearerToken = getAccessToken();
  const headers: Record<string, string> = {
    "X-CSRFToken": csrftoken ?? "",
    ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
  };

  const res = await fetch(
    `${API_BASE_URL}${ApiConfig.ACCOUNT_BASE}/profile/image/preview/`,
    {
      method: "POST",
      credentials: "include",
      headers,
      body: formData,
    },
  );

  if (!res.ok) {
    await handleResponse(res);
    throw new Error("Failed to preview profile image");
  }

  return await res.blob();
}

export async function updateSignature(
  signatureFile: File,
): Promise<RequiredActionResponse> {
  const formData = new FormData();
  formData.append("signature", signatureFile);
  return apiPatchFormData<RequiredActionResponse>(
    `${ApiConfig.ACCOUNT_BASE}/profile/signature/`,
    formData,
  );
}

export async function logout(): Promise<void> {
  await apiPost(`/logout/`, undefined, { keepalive: true });
}

export async function showPatientSticker(patientId: number): Promise<Response> {
  return apiGetResponse(
    `/v1/documents/patient-stickers/render/?patient_id=${patientId}`,
  );
}

export async function sendPatientStickerPrint(
  patientId: number,
  stickerCount = 1,
): Promise<void> {
  await apiPost("/v1/documents/patient-stickers/print/", {
    patient_id: patientId,
    sticker_count: stickerCount,
  });
}

export async function updateCurrentClinicBranch(
  branchId: number,
): Promise<UpdateProfileResponse> {
  return apiPatch<UpdateProfileResponse>(
    `${ApiConfig.ACCOUNT_BASE}/profile/`,
    { current_clinic_branch_id: branchId },
  );
}
